/*
 * <custom-image-select>
 *
 * Progressive enhancement over a real <select>: a listbox that can show a
 * thumbnail next to each option, which a native <select> cannot do.
 *
 * The <select> stays in the DOM as the form control and the source of truth —
 * choosing an option writes to it and dispatches a bubbling `change`, so
 * whatever already listens (Dawn's VariantSelects, <collection-list-select>)
 * keeps working untouched. Without JS the native dropdown is what renders.
 *
 * Thumbnails come from `data-image` on each <option>; `data-unavailable` dims
 * sold-out entries.
 */
if (!customElements.get('custom-image-select')) {
  class CustomImageSelect extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      if (!this.select || this.enhanced) return;

      this.enhanced = true;
      this.activeIndex = Math.max(this.select.selectedIndex, 0);
      this.build();
      this.bind();
      this.syncFromSelect();
      this.classList.add('custom-image-select--enhanced');
    }

    build() {
      if (!this.select.id) {
        this.select.id = `CustomImageSelect-${Math.random().toString(36).slice(2, 9)}`;
      }
      const id = this.select.id;

      this.label = document.querySelector(`label[for="${CSS.escape(id)}"]`);
      if (this.label && !this.label.id) this.label.id = `${id}-label`;

      this.value = document.createElement('span');
      this.value.className = 'custom-image-select__value';
      this.value.id = `${id}-value`;

      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'custom-image-select__trigger';
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', `${id}-listbox`);
      if (this.label) this.trigger.setAttribute('aria-labelledby', `${this.label.id} ${this.value.id}`);
      this.trigger.appendChild(this.value);

      // Reuse the caret the theme already rendered inside .select.
      const caret = this.querySelector('.select svg');
      if (caret) {
        const clone = caret.cloneNode(true);
        clone.removeAttribute('id');
        clone.classList.add('custom-image-select__caret');
        this.trigger.appendChild(clone);
      }

      this.list = document.createElement('ul');
      this.list.className = 'custom-image-select__list';
      this.list.id = `${id}-listbox`;
      this.list.tabIndex = -1;
      this.list.hidden = true;
      this.list.setAttribute('role', 'listbox');
      if (this.label) this.list.setAttribute('aria-labelledby', this.label.id);

      this.items = Array.from(this.select.options).map((option, index) => {
        const item = document.createElement('li');
        item.className = 'custom-image-select__option';
        item.id = `${id}-option-${index}`;
        item.dataset.value = option.value;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        if (option.dataset.unavailable) item.classList.add('custom-image-select__option--unavailable');
        item.appendChild(this.optionContent(option));
        this.list.appendChild(item);
        return item;
      });

      this.append(this.trigger, this.list);
    }

    // Built with DOM nodes rather than innerHTML — option text is merchant data.
    optionContent(option) {
      const fragment = document.createDocumentFragment();

      if (option.dataset.image) {
        const image = document.createElement('img');
        image.className = 'custom-image-select__thumb';
        image.src = option.dataset.image;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 48;
        image.height = 48;
        fragment.appendChild(image);
      }

      const text = document.createElement('span');
      text.className = 'custom-image-select__text';
      text.textContent = option.textContent.trim();
      fragment.appendChild(text);

      return fragment;
    }

    bind() {
      this.trigger.addEventListener('click', () => this.toggle());
      this.trigger.addEventListener('keydown', this.onTriggerKeydown.bind(this));
      this.list.addEventListener('keydown', this.onListKeydown.bind(this));
      this.list.addEventListener('click', (event) => {
        const item = event.target.closest('.custom-image-select__option');
        if (item) this.commit(this.items.indexOf(item));
      });

      // The variant can also change from Dawn, the URL or another picker.
      this.select.addEventListener('change', () => this.syncFromSelect());

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    }

    get isOpen() {
      return !this.list.hidden;
    }

    open(index = this.select.selectedIndex) {
      this.list.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.setActive(Math.max(index, 0));
      this.list.focus();
    }

    close({ focusTrigger = false } = {}) {
      if (!this.isOpen) return;
      this.list.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      if (focusTrigger) this.trigger.focus();
    }

    toggle() {
      this.isOpen ? this.close({ focusTrigger: true }) : this.open();
    }

    setActive(index) {
      this.activeIndex = Math.min(Math.max(index, 0), this.items.length - 1);
      const item = this.items[this.activeIndex];
      if (!item) return;

      this.items.forEach((other) => other.classList.remove('custom-image-select__option--active'));
      item.classList.add('custom-image-select__option--active');
      this.list.setAttribute('aria-activedescendant', item.id);
      item.scrollIntoView({ block: 'nearest' });
    }

    commit(index) {
      const item = this.items[index];
      if (!item) return;

      this.select.value = item.dataset.value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncFromSelect();
      this.close({ focusTrigger: true });
    }

    syncFromSelect() {
      const index = Math.max(this.select.selectedIndex, 0);
      const option = this.select.options[index];
      if (!option) return;

      this.items.forEach((item, itemIndex) => {
        item.setAttribute('aria-selected', itemIndex === index ? 'true' : 'false');
      });

      this.value.replaceChildren(this.optionContent(option));
      this.setActive(index);
    }

    onTriggerKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.open();
          break;
        default:
          break;
      }
    }

    onListKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setActive(this.activeIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setActive(this.activeIndex - 1);
          break;
        case 'Home':
          event.preventDefault();
          this.setActive(0);
          break;
        case 'End':
          event.preventDefault();
          this.setActive(this.items.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.commit(this.activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          this.close({ focusTrigger: true });
          break;
        case 'Tab':
          this.close();
          break;
        default:
          break;
      }
    }
  }

  customElements.define('custom-image-select', CustomImageSelect);
}
